import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CardWrapper,
    LogoLogin,
    PBemVindo,
    PAcessarSuaConta,
    CustomErrorMessage,
    FormContent,
    FieldGroup,
    PasswordFieldWrapper,
    PasswordToggleButton,
    InputFull,
    ActionsWrapper,
} from './AuthCard';
import { PrimaryTextColor, GreenAlert } from '../../../app/theme/tokens/colors';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { LoginService } from '../../../services/auth';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import styled from 'styled-components';

const loginService = LoginService;

const SuccessBox = styled.div`
  background: ${GreenAlert};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: ${PrimaryTextColor};
  white-space: pre-line;
  text-align: center;
`;

const RulesTitle = styled.p`
  margin: 8px 0 4px 0;
  font-size: 12px;
  color: ${PrimaryTextColor};
`;

const PasswordRules = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RuleItem = styled.li<{ valid: boolean }>`
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  border-radius: 6px;
  color: ${({ valid }) => (valid ? '#4a9e55' : PrimaryTextColor)};
  opacity: ${({ valid }) => (valid ? 1 : 0.7)};
`;

const RuleIcon = styled.span<{ valid: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  font-size: 8px;
  flex-shrink: 0;
  border: 1.5px solid ${({ valid }) => (valid ? '#4a9e55' : '#cbd5e0')};
  color: ${({ valid }) => (valid ? '#4a9e55' : 'transparent')};
`;

const ValidationMessage = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${PrimaryTextColor};
`;

const safeDecodeParam = (val: string | null) => {
    if (!val) return val;
    try {
        let decoded = decodeURIComponent(val);
        if (decoded && decoded.indexOf('%') >= 0) {
            try {
                decoded = decodeURIComponent(decoded);
            } catch (e) {
                console.error('Error decoding param:', e);
            }
        }
        return decoded;
    } catch {
        return val;
    }
};

export const EmailResetPasswordCard: React.FC = () => {
    const [showPasswordLocal, setShowPasswordLocal] = useState(false);
    const [showRulesAfterInvalidSubmit, setShowRulesAfterInvalidSubmit] =
        useState(false);
    const [searchParams] = useSearchParams();

    const [newPasswordLocal, setNewPasswordLocal] = useState('');
    const [messageLocal, setMessageLocal] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const code = safeDecodeParam(searchParams.get('code'));
    const username = safeDecodeParam(searchParams.get('username'));

    const invalidLink = !code || !username;

    const passwordChecks = {
        minLength: newPasswordLocal.length >= 8,
        upperCase: /[A-Z]/.test(newPasswordLocal),
        lowerCase: /[a-z]/.test(newPasswordLocal),
        number: /\d/.test(newPasswordLocal),
        special: /[^A-Za-z0-9]/.test(newPasswordLocal),
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);
    const shouldShowRules = showRulesAfterInvalidSubmit && !isPasswordValid;

    const rules = [
        {
            key: 'minLength',
            label: 'Mínimo de 8 caracteres',
            valid: passwordChecks.minLength,
        },
        {
            key: 'upperCase',
            label: 'Letra maiúscula',
            valid: passwordChecks.upperCase,
        },
        {
            key: 'lowerCase',
            label: 'Letra minúscula',
            valid: passwordChecks.lowerCase,
        },
        { key: 'number', label: 'Número', valid: passwordChecks.number },
        {
            key: 'special',
            label: 'Caractere especial',
            valid: passwordChecks.special,
        },
    ];

    useEffect(() => {
        if (invalidLink) {
            setIsSuccess(false);
            setMessageLocal(
                'Link de redefinição inválido ou incompleto.\nAcesse novamente a partir do e-mail recebido ou solicite um novo link.'
            );
        }
    }, [invalidLink]);

    const goToLogin = () => {
        window.location.href = '/login';
    };

    const handleResetPassword = async () => {
        if (invalidLink) {
            setIsSuccess(false);
            setMessageLocal(
                'Link de redefinição inválido ou expirado.\nSolicite uma nova redefinição de senha.'
            );
            return;
        }

        if (!newPasswordLocal) {
            setIsSuccess(false);
            setMessageLocal('Informe a nova senha.');
            return;
        }

        if (!isPasswordValid) {
            setShowRulesAfterInvalidSubmit(true);
            setIsSuccess(false);
            setMessageLocal(
                'Sua senha não atende aos requisitos mínimos de segurança.'
            );
            return;
        }

        setShowRulesAfterInvalidSubmit(false);

        setLoading(true);
        try {
            const result = await loginService.recoveryPassword(
                username as string,
                newPasswordLocal,
                code as string
            );

            if (result && result.sucess) {
                setIsSuccess(true);
                setMessageLocal(
                    'Sua senha foi redefinida com sucesso!\nVocê já pode fazer login novamente no Portal Homecare.'
                );

                setTimeout(() => {
                    goToLogin();
                }, 3000);
            } else {
                const errorMsg =
                    result && result.message
                        ? String(result.message)
                        : 'Erro ao tentar redefinir a senha.\nCaso o problema persista, entre em contato com o suporte.';
                setIsSuccess(false);
                setMessageLocal(errorMsg);
            }
        } catch (err) {
            console.error('[emailResetPassword] erro:', err);
            setIsSuccess(false);
            setMessageLocal(
                'Erro ao tentar redefinir a senha.\nCaso o problema persista, entre em contato com o suporte.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <CardWrapper>
            <LogoLogin />

            <PBemVindo>
                Bem-vindo ao <b>Portal Homecare</b>
            </PBemVindo>
            <PAcessarSuaConta>Redefinir senha</PAcessarSuaConta>

            {messageLocal &&
                (isSuccess ? (
                    <SuccessBox>{messageLocal}</SuccessBox>
                ) : (
                    <CustomErrorMessage style={{ whiteSpace: 'pre-line' }}>
                        {messageLocal}
                    </CustomErrorMessage>
                ))}

            {invalidLink ? (
                <ActionsWrapper>
                    <SecondaryButton onClick={goToLogin}>
                        Voltar para login
                    </SecondaryButton>
                </ActionsWrapper>
            ) : (
                <FormContent>
                    <FieldGroup>
                        <label
                            style={{
                                fontSize: 14,
                                color: PrimaryTextColor,
                                textAlign: 'left',
                            }}
                        >
                            Nova senha
                        </label>
                        <PasswordFieldWrapper>
                            <InputFull
                                type={showPasswordLocal ? 'text' : 'password'}
                                placeholder="Digite a nova senha"
                                value={newPasswordLocal}
                                onChange={(e) => setNewPasswordLocal(e.target.value)}
                            />
                            <PasswordToggleButton
                                type="button"
                                onClick={() => setShowPasswordLocal((prev) => !prev)}
                                aria-label={
                                    showPasswordLocal ? 'Ocultar senha' : 'Mostrar senha'
                                }
                            >
                                {showPasswordLocal ? <FaEyeSlash /> : <FaEye />}
                            </PasswordToggleButton>
                        </PasswordFieldWrapper>

                        {shouldShowRules && (
                            <div>
                                <RulesTitle>A senha deve conter:</RulesTitle>
                                <PasswordRules>
                                    {rules.map(({ key, label, valid }) => (
                                        <RuleItem key={key} valid={valid}>
                                            <RuleIcon valid={valid}>{valid ? '✓' : ''}</RuleIcon>
                                            {label}
                                        </RuleItem>
                                    ))}
                                </PasswordRules>
                                <ValidationMessage>
                                    Sua senha não atende aos requisitos mínimos de segurança.
                                </ValidationMessage>
                            </div>
                        )}
                    </FieldGroup>

                    <ActionsWrapper>
                        <PrimaryButton onClick={handleResetPassword} disabled={loading}>
                            {loading ? 'Redefinindo...' : 'Redefinir senha'}
                        </PrimaryButton>

                        <SecondaryButton onClick={goToLogin}>
                            Voltar para login
                        </SecondaryButton>
                    </ActionsWrapper>
                </FormContent>
            )}
        </CardWrapper>
    );
};
